import reportsalesService from '../../../services/reportsalesService';
import filterService from '../../../services/filterService';

import {
    setReportsalesLoading,
    setReportsalesError,
    setReportsalesList,
    setReportsalesCurrent,
    setReportsalesPagination,
    setReportsalesSort,
    setReportsalesFilters,
    setReportsalesDefaultFilter,
    setReportsalesSettings,
    clearReportsalesCurrent,
    clearReportsalesError,
    setReportsalesFilterLoading,
    changeReportsalesFilterCondition,
    addReportsalesFilterCondition,
    updateReportsalesFilterConditionRule,
    updateReportsalesFilterConditionValue,
    removeReportsalesFilterCondition,
    setReportsalesSelected,
    setReportsalesDownloading,
    resetReportsalesFiltersAndPagination,

    setReportsalesClassicFilters
} from './reportsalesSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        if (!getState().reportsales.isSettingsInitialized) {
            dispatch(setReportsalesLoading(true));
            const response = await reportsalesService.getSettings();

            dispatch(setReportsalesSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setReportsalesClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setReportsalesDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setReportsalesError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setReportsalesLoading(false));
    }
};

export const fetchReportsales = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        const state = getState().reportsales;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setReportsalesLoading(true));
        const response = await reportsalesService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setReportsalesList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchReportsales:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportsalesError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportsalesLoading(false));
    }
};

export const fetchReportsale = (id) => async (dispatch) => {
    try {
        dispatch(setReportsalesLoading(true));
        const response = await reportsalesService.getOne(id);
        dispatch(setReportsalesCurrent(response.data));
    } catch (error) {
        dispatch(setReportsalesError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setReportsalesLoading(false));
    }
};

export const createReportsale = (data) => async (dispatch) => {
    try {
        const response = await reportsalesService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateReportsale = (id, data) => async (dispatch) => {
    try {
        const response = await reportsalesService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteReportsale = (id) => async (dispatch) => {
    try {
        dispatch(setReportsalesLoading(true));
        const response = await reportsalesService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setReportsalesError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportsalesLoading(false));
    }
};

export const downloadReportsale = (type) => async (dispatch, getState) => {
    try {
        const state = getState().reportsales;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setReportsalesDownloading(true));
        const response = await reportsalesService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setReportsalesDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading reportsales:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportsalesError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportsalesDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setReportsalesPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setReportsalesSort(sort));
};

export const searchReportsales = (query) => (dispatch, getState) => {
    const currentFilters = getState().reportsales.filters;
    dispatch(setReportsalesFilters({ ...currentFilters, search: query }));
};

export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setReportsalesDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeReportsalesFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addReportsalesFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateReportsalesFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateReportsalesFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeReportsalesFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setReportsalesFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().reportsales.settings;
            dispatch(setReportsalesSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setReportsalesFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setReportsalesFilterLoading(false));
        dispatch(setReportsalesError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setReportsalesSelected(selectedRows));
    };
};

export const clearCurrentReportsale = () => {
    return (dispatch) => {
        dispatch(clearReportsalesCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetReportsalesFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().reportsales.classicFilters;

    const updatedClassicFilters = { ...currentClassicFilters };

    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setReportsalesClassicFilters(updatedClassicFilters));
};